import { useCallback, useRef, useState } from 'react';

const HIGHLIGHT_NAME = 'remix-thinking-renderer';

/**
 * Persists a visual highlight on the user's current text selection in the
 * renderer (view page) using the CSS Custom Highlight API. The highlight
 * survives after the browser selection is cleared, matching the purple
 * "thinking" style used in the editor's remix-thinking-plugin.
 *
 * Pair with <RendererRemixHighlightStyles /> rendered once at the page
 * level to provide the ::highlight() CSS rule.
 */
export const useRendererRemixHighlight = () => {
	const savedRangeRef = useRef<Range | null>(null);
	const [isHighlighting, setIsHighlighting] = useState(false);

	const startHighlight = useCallback(() => {
		const sel = document.getSelection();
		if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
			return;
		}

		const range = sel.getRangeAt(0).cloneRange();
		savedRangeRef.current = range;

		if (typeof Highlight !== 'undefined' && 'highlights' in CSS) {
			const highlight = new Highlight(range);
			(CSS as any).highlights.set(HIGHLIGHT_NAME, highlight);
			sel.removeAllRanges();
			setIsHighlighting(true);
		}
	}, []);

	const clearHighlight = useCallback(() => {
		if (typeof Highlight !== 'undefined' && 'highlights' in CSS) {
			(CSS as any).highlights.delete(HIGHLIGHT_NAME);
		}
		savedRangeRef.current = null;
		setIsHighlighting(false);
	}, []);

	return { startHighlight, clearHighlight, isHighlighting, savedRange: savedRangeRef };
};
